/**
 * tRPC Provider for Auth Portal
 *
 * Provides tRPC context and React Query client to the application.
 * Supports both standalone mode (creates own QueryClient) and integration mode
 * (uses existing QueryClient from QueryClientProvider).
 *
 * Includes global error logging for debugging API issues.
 *
 * @module lib/trpc/provider
 */

import { useTracing } from '@ops-matrix/design-system-ui/tracing';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createDebugLogger } from '../../utils/debug';
import { globalCircuitBreaker } from './circuit-breaker';
import { createTRPCClient, setTraceHeadersGetter, trpc } from './client';
import { getRetryDelay, retryWithBackoff } from './retry-config';

const debug = createDebugLogger('tRPC');

interface TRPCProviderProps {
  children: React.ReactNode;
  /** Optional external QueryClient. If not provided, creates its own. */
  queryClient?: QueryClient;
}

/**
 * Create query client with production-grade resilience
 *
 * Features:
 * - Circuit breaker to prevent thundering herd
 * - Exponential backoff with jitter (1s → 2s → 4s → 8s → max 30s)
 * - Smart retry logic (retry network/server errors, skip auth/client errors)
 * - Request deduplication via increased staleTime
 */
function createQueryClientWithLogging(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Request deduplication - cache queries for 30s
        staleTime: 30000, // 30 seconds (was 5 minutes)
        gcTime: 5 * 60 * 1000, // 5 minutes

        // Smart retry with exponential backoff
        retry: retryWithBackoff, // Max 5 retries with circuit breaker
        retryDelay: getRetryDelay, // 1s → 2s → 4s → 8s → 30s

        // Refetch behavior
        refetchOnWindowFocus: false, // Don't refetch on tab focus
        refetchOnMount: false, // Use cached data on mount
        refetchOnReconnect: true, // DO refetch when network reconnects
      },
      mutations: {
        // Mutations should not retry by default (could cause duplicate actions)
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Record failure in circuit breaker
        globalCircuitBreaker.recordFailure();

        const queryKey = query.queryKey;
        const endpoint = Array.isArray(queryKey) ? queryKey.join('.') : String(queryKey);
        debug.error(`Query failed: ${endpoint}`, error, {
          queryKey,
          state: query.state.status,
          circuitState: globalCircuitBreaker.getState(),
          failureCount: globalCircuitBreaker.getFailureCount(),
        });
      },
      onSuccess: (_data, query) => {
        // Record success in circuit breaker
        globalCircuitBreaker.recordSuccess();

        // Debug log for successful queries (dev only).
        // Vite portals must use `import.meta.env.DEV` — bare `process.env.X`
        // is not replaced at build time, so it ships as-is to the browser
        // and throws `process is not defined` on the first tRPC success.
        if (import.meta.env.DEV) {
          const queryKey = query.queryKey;
          const endpoint = Array.isArray(queryKey) ? queryKey.join('.') : String(queryKey);
          debug.info(`Query succeeded: ${endpoint}`, { circuitState: globalCircuitBreaker.getState() });
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Don't affect circuit breaker for mutations (user-initiated actions)
        const mutationKey = mutation.options.mutationKey;
        const endpoint = Array.isArray(mutationKey) ? mutationKey.join('.') : 'unknown';
        debug.error(`Mutation failed: ${endpoint}`, error, { mutationKey });
      },
    }),
  });
}

/**
 * tRPC Provider (Integration Mode)
 *
 * Use this when you already have a QueryClientProvider in your app.
 * It will use the existing QueryClient for tRPC queries.
 *
 * @example
 * ```tsx
 * // In main.tsx - wrap inside existing QueryClientProvider
 * <QueryClientProvider client={queryClient}>
 *   <TRPCProviderInternal>
 *     <App />
 *   </TRPCProviderInternal>
 * </QueryClientProvider>
 * ```
 */
export function TRPCProviderInternal({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [trpcClient] = useState(() => createTRPCClient());

  // Connect tracing to tRPC client for W3C TraceContext header propagation
  const { getTraceHeaders } = useTracing();
  useEffect(() => {
    setTraceHeadersGetter(getTraceHeaders);
  }, [getTraceHeaders]);

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      {children}
    </trpc.Provider>
  );
}

/**
 * tRPC Provider (Standalone Mode)
 *
 * Creates its own QueryClient and QueryClientProvider.
 * Use this when tRPC is the primary data fetching layer.
 *
 * @example
 * ```tsx
 * // In main.tsx - standalone mode
 * <TRPCProvider>
 *   <App />
 * </TRPCProvider>
 * ```
 */
export function TRPCProvider({ children, queryClient: externalQueryClient }: TRPCProviderProps): React.ReactElement {
  const [internalQueryClient] = useState(() => externalQueryClient ?? createQueryClientWithLogging());

  const queryClient = externalQueryClient ?? internalQueryClient;
  const [trpcClient] = useState(() => createTRPCClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}

export default TRPCProvider;
